/**
 * @fileoverview
 * Notification Service - High-level service for notifications
 * 
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { Injectable, inject } from '@angular/core';
import { NotificationStore } from '../state/stores';
import { NotificationType, NotificationPosition } from '../state/models';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notificationStore = inject(NotificationStore);

  // Expose store's signals
  readonly notifications = this.notificationStore.notifications;
  readonly unreadCount = this.notificationStore.unreadCount;
  readonly hasUnread = this.notificationStore.hasUnread;
  readonly recentNotifications = this.notificationStore.recentNotifications;

  /**
   * Show success notification
   */
  success(message: string, title: string = 'Success'): void {
    this.notificationStore.success(title, message, {
      duration: 3000,
      position: NotificationPosition.TOP_RIGHT
    });
  }

  /**
   * Show error notification
   */
  error(message: string, title: string = 'Error'): void {
    this.notificationStore.error(title, message, {
      duration: 5000,
      position: NotificationPosition.TOP_RIGHT
    });
  }

  /**
   * Show warning notification
   */
  warning(message: string, title: string = 'Warning'): void {
    this.notificationStore.warning(title, message, {
      duration: 4000,
      position: NotificationPosition.TOP_RIGHT
    });
  }

  /**
   * Show info notification
   */
  info(message: string, title: string = 'Info'): void {
    this.notificationStore.info(title, message, {
      duration: 3000,
      position: NotificationPosition.TOP_RIGHT
    });
  }

  /**
   * Show custom notification
   */
  show(
    type: NotificationType,
    title: string,
    message: string,
    options?: {
      duration?: number;
      position?: NotificationPosition;
      actionUrl?: string;
      actionLabel?: string;
    }
  ): string {
    return this.notificationStore.add(type, title, message, options);
  }

  /**
   * Remove notification
   */
  remove(id: string): void {
    this.notificationStore.remove(id);
  }

  /**
   * Mark as read
   */
  markAsRead(id: string): void {
    this.notificationStore.markAsRead(id);
  }

  /**
   * Mark all as read
   */
  markAllAsRead(): void {
    this.notificationStore.markAllAsRead();
  }

  /**
   * Clear all notifications
   */
  clear(): void {
    this.notificationStore.clear();
  }

  /**
   * Clear read notifications
   */
  clearRead(): void {
    this.notificationStore.clearRead();
  }
}

